import {
  ActionIcon,
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Divider,
  Flex,
  Grid,
  Group,
  Loader,
  Paper,
  Radio,
  Text,
  useMantineTheme,
  Drawer,
  Badge,
  Stack,
  ThemeIcon,
  ScrollArea,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { 
  IconExclamationCircle, 
  IconPlus, 
  IconSearch, 
  IconX, 
  IconFilter, 
  IconChevronRight,
  IconCategory
} from '@tabler/icons-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/master.service';
import { image_url, is_mobile_view } from '../config';
import InfiniteScroll from 'react-infinite-scroll-component';
import ProductCard from '../components/ProductCard';
import { showErrorToast, showSuccessToast } from '../utilities/Toast';
import CustomSlider from '../components/CustomSlider';
import { CartContext } from '../App';
import { qtyCalculator } from '../utilities/Common';
import { motion, AnimatePresence } from 'framer-motion';

function CategoryPage() {
  let isMobileScreen = useMediaQuery('(max-width: 768px)');
  isMobileScreen = is_mobile_view ? is_mobile_view : isMobileScreen;
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setCartItemCount } = useContext(CartContext);

  const [subCategories, setSubCategories] = useState([]);
  const [title, setTitle] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [offerBanners, setOfferBanners] = useState([]);
  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);

  const isSmallDevice = useMediaQuery("only screen and (max-width : 768px)");
  const isMediumDevice = useMediaQuery("only screen and (min-width : 769px) and (max-width : 992px)");
  const isLargeDevice = useMediaQuery("only screen and (min-width : 993px) and (max-width : 1200px)");
  const isExtraLargeDevice = useMediaQuery("only screen and (min-width : 1201px)");
  
  const productRequest = isSmallDevice || isMediumDevice ? 10 : isLargeDevice ? 12 : isExtraLargeDevice ? 16 : 12;

  useEffect(() => {
    fetchInitialProducts();
    fetchCategories();
    fetchBanners();
  }, [searchParams, selectedSubCategory]);

  const fetchInitialProducts = async () => {
    setIsLoading(true);
    try {
      const res = await api.get_products(1, productRequest, '', searchParams.get('id'), selectedSubCategory, null);
      setDisplayProducts(res.data.data || []);
      setPage(1);
      setHasMore(res.data.data?.length >= productRequest);
    } catch (err) {
      console.error(err);
    }
    setIsLoading(false);
  };

  const fetchCategories = async () => {
    const categoryId = searchParams.get('id');
    const res = await api.get_categories();
    const category = res.data.data.find((e) => e.id === +categoryId);
    if (category) {
      setTitle(category.title);
      // Simplified: Just set subcategories. Complex counting could be done if needed.
      setSubCategories(category.sub_category || []);
    }
  };

  const fetchBanners = async () => {
    const res = await api.get_banners();
    setOfferBanners(res.data.data.filter((e) => e.offer_banner === true));
  };

  const fetchData = async () => {
    const nextPage = page + 1;
    try {
      const res = await api.get_products(nextPage, productRequest, '', searchParams.get('id'), selectedSubCategory, null);
      if (res.data.data?.length > 0) {
        setDisplayProducts([...displayProducts, ...res.data.data]);
        setPage(nextPage);
        setHasMore(res.data.data.length >= productRequest);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      setHasMore(false);
    }
  };

  const addToCart = async (id, varient, unit="pkt", isVarient) => {
    const res = await api.add_to_cart({
      product_id: id,
      quantity: qtyCalculator(1, varient, unit, isVarient),
      varientSlug: `${varient}${unit} x 1`
    });
    if (res.data.success) {
      const data = res.data.data;
      setCartItemCount((data.cartitem?.filter(i => !i.bundle).length || 0) + (data.cart_bundles?.length || 0));
      showSuccessToast({ title: 'Success', message: 'Added to cart' });
    } else {
      if (res.data.code === 401) navigate('/login');
      else showErrorToast({ title: 'Error', message: res.data.message });
    }
  };

  return (
    <div style={{ backgroundColor: theme.colors.gray[0], minHeight: '100vh' }}>
      <Container size="xl" pt={isMobileScreen ? 140 : 180} pb={40}>
        {/* BREADCRUMBS */}
        <Box mb={20} px={10}>
          <Breadcrumbs separator={<IconChevronRight size={12} />}>
            <Link to="/" style={{ color: theme.colors.gray[6], textDecoration: 'none', fontSize: 13 }}>Home</Link>
            <Link to="/categories" style={{ color: theme.colors.gray[6], textDecoration: 'none', fontSize: 13 }}>Categories</Link>
            <Text size={13} fw={700}>{title}</Text>
          </Breadcrumbs>
        </Box>

        {/* HERO BANNERS */}
        {offerBanners.length > 0 && (
          <Box
            mb={isMobileScreen ? 24 : 42}
            sx={{
              overflow: 'hidden',
              '.slick-list': {
                margin: isMobileScreen ? 0 : '0 -10px',
              },
              '.slick-slide > div': {
                padding: isMobileScreen ? 0 : '0 10px',
              },
            }}
          >
            <CustomSlider slidesToShow={isMobileScreen ? 1 : 2} hideArrows>
              {offerBanners.map((e) => (
                <Paper
                  key={e.id}
                  radius={isMobileScreen ? 8 : 14}
                  sx={{
                    overflow: 'hidden',
                    backgroundColor: '#ffffff',
                    boxShadow: isMobileScreen ? theme.shadows.xs : theme.shadows.sm,
                    cursor: e.product ? 'pointer' : 'default',
                    aspectRatio: isMobileScreen ? '3 / 1' : 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  onClick={() => e.product && navigate(`/product?id=${e.product}`)}
                >
                  <img
                    src={image_url + e.image}
                    alt="Category banner"
                    style={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      objectFit: isMobileScreen ? 'contain' : 'cover',
                    }}
                  />
                </Paper>
              ))}
            </CustomSlider>
          </Box>
        )}

        {/* HEADER & SUB-CATEGORY CHIPS */}
        <Box mb={30} px={10}>
          <Flex justify="space-between" align="center" mb="lg">
            <Text fw={800} size={28} tt="capitalize">{title}</Text>
            <Button 
              variant="light" 
              leftIcon={<IconFilter size={18} />} 
              onClick={() => setFilterDrawerOpened(true)}
              radius="md"
            >
              Filters
            </Button>
          </Flex>

          <ScrollArea scrollbarWidth="none" sx={{ width: '100%' }}>
            <Group spacing="sm" noWrap>
              <Button 
                variant={selectedSubCategory === '' ? 'filled' : 'outline'} 
                radius="xl" 
                size="sm"
                onClick={() => setSelectedSubCategory('')}
              >
                All
              </Button>
              {subCategories.map((sub) => (
                <Button
                  key={sub.id}
                  variant={selectedSubCategory === sub.id ? 'filled' : 'outline'}
                  radius="xl"
                  size="sm"
                  onClick={() => setSelectedSubCategory(sub.id)}
                >
                  {sub.title}
                </Button>
              ))}
            </Group>
          </ScrollArea>
        </Box>

        {/* PRODUCT GRID */}
        <Box px={10}>
          {!isLoading && displayProducts.length === 0 ? (
            <Paper p={60} radius="lg" sx={{ textAlign: 'center', backgroundColor: 'transparent' }}>
              <ThemeIcon size={80} radius="xl" variant="light" color="gray" mb={20}>
                <IconExclamationCircle size={40} />
              </ThemeIcon>
              <Text fw={700} size="xl">No Products Found</Text>
              <Text color="dimmed">Try adjusting your filters or search string</Text>
            </Paper>
          ) : (
            <InfiniteScroll
              dataLength={displayProducts.length}
              next={fetchData}
              hasMore={hasMore}
              loader={
                <Flex justify="center" py={40}>
                  <Loader variant="dots" />
                </Flex>
              }
              endMessage={
                <Divider 
                  my="xl" 
                  label={<Text color="dimmed">You've reached the end</Text>} 
                  labelPosition="center" 
                />
              }
              style={{ overflow: 'visible' }}
            >
              <Grid gutter="md">
                <AnimatePresence>
                  {displayProducts.map((product, idx) => (
                    <Grid.Col key={product.id} span={6} sm={4} md={3} lg={2.4}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: (idx % 10) * 0.05 }}
                      >
                        <ProductCard object={product} addToCart={addToCart} />
                      </motion.div>
                    </Grid.Col>
                  ))}
                </AnimatePresence>
              </Grid>
            </InfiniteScroll>
          )}
        </Box>
      </Container>

      {/* FILTER DRAWER */}
      <Drawer
        opened={filterDrawerOpened}
        onClose={() => setFilterDrawerOpened(false)}
        title={<Text fw={800} size="lg">Filter Products</Text>}
        padding="xl"
        size="sm"
        position="right"
      >
        <Stack spacing="xl">
          <Box>
            <Text fw={700} size="sm" color="dimmed" mb="md" tt="uppercase">Sub Categories</Text>
            <Radio.Group value={selectedSubCategory} onChange={(val) => { setSelectedSubCategory(+val); setFilterDrawerOpened(false); }}>
              <Stack spacing="xs">
                <Radio value="" label="All Categories" />
                {subCategories.map((sub) => (
                  <Radio key={sub.id} value={sub.id.toString()} label={sub.title} />
                ))}
              </Stack>
            </Radio.Group>
          </Box>
          
          <Divider />
          
          <Button variant="light" color="red" fullWidth leftIcon={<IconX size={16} />} onClick={() => { setSelectedSubCategory(''); setFilterDrawerOpened(false); }}>
            Clear All Filters
          </Button>
        </Stack>
      </Drawer>
    </div>
  );
}

export default CategoryPage;
